/**
 * KIEM TRA PHU GLYPH - cua chan bat buoc truoc moi lan sua.
 *
 * Port tu tools/text-edit-prototype/src/fonts/coverage.mjs.
 *
 * =============================================================================
 *  VI SAO PHEP KIEM NAY LA PHEP KIEM DUY NHAT CO GIA TRI
 * =============================================================================
 *  Mot phep kiem "dem U+FFFD / U+0000 trong van ban trich xuat duoc" KHONG
 *  CHUNG MINH DUOC GI CA. Bang /ToUnicode va glyph thuc su duoc ve la HAI thu
 *  doc lap trong PDF: mot trang toan o vuong trong van trich xuat ra chuoi
 *  hoan hao neu /ToUnicode dung. Do la mot trong bon kieu "thanh cong gia".
 *
 *  Chi mot phep kiem chung minh duoc dieu gi: doi chieu MA UNICODE voi CHUONG
 *  TRINH FONT that, va bat buoc glyph id khac 0 (.notdef). Do la ham hasGlyph()
 *  ben duoi va do la ly do module nay ton tai.
 *
 * =============================================================================
 *  VI SAO TIENG VIET LA PHEP THU KHAC NGHIET NHAT
 * =============================================================================
 *  Tieng Viet chong hai tang dau tren mot nguyen am: dau am (a-mu, a-trang,
 *  o-moc) cong dau thanh (huyen, sac, hoi, nga, nang). Cho ra 134 ky tu ngoai
 *  ASCII. Mot font Latin "day du" theo chuan phuong Tay (Latin-1) VAN THIEU gan
 *  het bang nay. Do tren 284 tai lieu that: do phu tieng Viet cua font subset
 *  co TRUNG VI 0%, va chi 11.5% font subset du glyph de go lai tieng Viet.
 *
 *  HAI DANG CHUAN HOA: "e-mu-sac" viet duoc theo hai cach - NFC (U+1EBF, mot ky
 *  tu) va NFD (U+0065 U+0302 U+0301, ba ky tu). Font co the ho tro dang nay ma
 *  khong ho tro dang kia, nen ta kiem CA HAI va bao ro dang nao dung duoc: tang
 *  dat chu phai biet nen gui dang nao xuong font.
 */
import type { Font } from './fontkit';

const chr = (cp: number): string => String.fromCodePoint(cp);

/** 12 nguyen am co so cua tieng Viet, viet bang ma so de ma nguon giu 100% ASCII. */
const VOWEL_BASES = [
  0x0061, 0x0103, 0x00e2, 0x0065, 0x00ea, 0x0069, 0x006f, 0x00f4, 0x01a1, 0x0075, 0x01b0, 0x0079,
].map(chr);

/** 6 thanh: ngang, huyen U+0300, sac U+0301, nga U+0303, hoi U+0309, nang U+0323. */
const TONE_MARKS = ['', ...[0x0300, 0x0301, 0x0303, 0x0309, 0x0323].map(chr)];

function buildVietnameseSet(): Set<string> {
  const out = new Set<string>();
  for (const base of VOWEL_BASES) {
    for (const tone of TONE_MARKS) {
      const composed = (base + tone).normalize('NFC');
      if (composed.length !== 1) continue;
      out.add(composed);
      const upper = composed.toUpperCase();
      if (upper.length === 1) out.add(upper);
    }
  }
  out.add(chr(0x0111)); // d gach ngang thuong
  out.add(chr(0x0110)); // D gach ngang hoa
  return out;
}

const VIET_ALL = buildVietnameseSet();

/** 134 ky tu tieng Viet nam ngoai ASCII - phep thu quyet dinh. */
export const VIET_NON_ASCII: string[] = [...VIET_ALL]
  .filter((c) => (c.codePointAt(0) ?? 0) > 0x7f)
  .sort();

/** Toan bo bang chu cai tieng Viet, ke ca dang khong dau. */
export const VIET_ALPHABET: string[] = [...VIET_ALL].sort();

/** ASCII in duoc 0x20..0x7E. */
export const ASCII_PRINTABLE: string[] = Array.from({ length: 0x7f - 0x20 }, (_, i) =>
  String.fromCharCode(0x20 + i),
);

/** Bang chu cai "go lai thuc te" cua mot nguoi dung Viet Nam. */
export const RETYPE_ALPHABET: string[] = [
  ...new Set([...ASCII_PRINTABLE, ...VIET_ALPHABET]),
].sort();

export type NormalizationForm = 'NFC' | 'NFD';

export interface CoverageResult {
  ok: boolean;
  /** Dang chuan hoa dung duoc. null khi khong dang nao du glyph. */
  form: NormalizationForm | null;
  /** Dang tot nhat da thu, ke ca khi van thieu. */
  bestForm: NormalizationForm;
  /** Ma Unicode thieu glyph, da sap xep. */
  missing: number[];
  total: number;
  present: number;
  ratio: number;
}

/**
 * Font co glyph THAT cho mot ma Unicode hay khong.
 *
 * Khong tin mot minh hasGlyphForCodePoint: mot so font tra ve true nhung anh xa
 * ve glyph 0 (.notdef) - tuc la thuc te ve ra o vuong trong. Ta kiem ca hai.
 */
export function hasGlyph(font: Font, codePoint: number): boolean {
  try {
    if (!font.hasGlyphForCodePoint(codePoint)) return false;
    const g = font.glyphForCodePoint(codePoint);
    return Boolean(g) && g.id !== 0;
  } catch {
    return false;
  }
}

interface Evaluation {
  uniqueCount: number;
  missing: number[];
}

function evaluate(font: Font, s: string): Evaluation {
  const uniq = new Set<number>();
  for (const ch of s) uniq.add(ch.codePointAt(0) ?? 0);
  const missing: number[] = [];
  for (const cp of uniq) {
    // Xuong dong va tab khong can glyph - tang dat chu xu ly chung.
    if (cp === 0x0a || cp === 0x0d || cp === 0x09) continue;
    if (!hasGlyph(font, cp)) missing.push(cp);
  }
  return { uniqueCount: uniq.size, missing: missing.sort((a, b) => a - b) };
}

/** Kiem tra mot font phu duoc mot chuoi muc tieu den dau. */
export function checkCoverage(font: Font, text: string): CoverageResult {
  const nfc = evaluate(font, text.normalize('NFC'));
  // Chi thu NFD khi NFC thieu: NFD hau nhu luon te hon vi no doi font co dau
  // ket hop roi, thu ma phan lon font Latin khong co.
  const nfd = nfc.missing.length > 0 ? evaluate(font, text.normalize('NFD')) : null;

  const useNfd = nfd !== null && nfd.missing.length < nfc.missing.length;
  const bestForm: NormalizationForm = useNfd ? 'NFD' : 'NFC';
  const best = useNfd && nfd ? nfd : nfc;
  const present = best.uniqueCount - best.missing.length;

  return {
    ok: best.missing.length === 0,
    form: best.missing.length === 0 ? bestForm : null,
    bestForm,
    missing: best.missing,
    total: best.uniqueCount,
    present,
    ratio: best.uniqueCount > 0 ? present / best.uniqueCount : 1,
  };
}

/** Do phu mot bang chu cai co san (khong phai mot chuoi cu the). */
export function alphabetCoverage(
  font: Font,
  alphabet: readonly string[],
): { present: number; total: number; ratio: number; missing: number[] } {
  let present = 0;
  const missing: number[] = [];
  for (const ch of alphabet) {
    const cp = ch.codePointAt(0) ?? 0;
    if (hasGlyph(font, cp)) present++;
    else missing.push(cp);
  }
  return {
    present,
    total: alphabet.length,
    ratio: alphabet.length > 0 ? present / alphabet.length : 0,
    missing,
  };
}

/**
 * Mo ta ky tu thieu, an toan de hien len man hinh.
 *
 * Chi tra ve MA UNICODE, khong bao gio ghep lai thanh van ban tai lieu: chuoi
 * nay di vao thong bao loi, va thong bao loi thi di vao nhat ky.
 */
export function describeMissing(codePoints: readonly number[], limit = 12): string {
  const shown = codePoints
    .slice(0, limit)
    .map((cp) => `U+${cp.toString(16).toUpperCase().padStart(4, '0')}`);
  const more = codePoints.length > limit ? ` (và ${codePoints.length - limit} ký tự nữa)` : '';
  return shown.join(', ') + more;
}
